// GAVION ai-email: the front port of the AI assistant. Real: gavion-ai-draft / gavion-ai-style / RPCs. Demo: the fake
// drafter over FakeRest. Errors are stable codes (AiApiError), never draft text.
import type { Lang } from "../../../../../supabase/functions/_shared/ai/types.ts";

export type AiDraftRequest = {
  companyId: number;
  contactId: number | null;
  dealId: number | null;
  instruction: string;
  /** 0 for the first draft, +1 for each « Régénérer ». */
  variant: number;
};
export type AiDraftResult = {
  draftId: number;
  subject: string;
  body: string;
  rationale: string;
  language: Lang;
};
export type RejectReason =
  | "not_relevant"
  | "wrong_tone"
  | "factual_error"
  | "other";
export const REJECT_REASONS: readonly RejectReason[] = [
  "not_relevant",
  "wrong_tone",
  "factual_error",
  "other",
];
export type StyleRebuild = {
  status: "built" | "too_few";
  sourceCount: number;
};

// GAVION call-brief-pipeline (spec A3–A4): the pre-call brief.
export type CallBriefRow = {
  companyId: number;
  whatTheyDo: string | null;
  hook: string | null;
  status: "ok" | "failed";
  generatedAt: string;
  websiteUsed: boolean;
};
export type CallBriefRequestOutcome =
  | "ok"
  | "disabled"
  | "cap"
  | "not_configured"
  | "error";
/** `capped`: the ids the edge did not prepare because the daily cap was reached (ruling 9). */
export type CallBriefRequestResult = {
  outcome: CallBriefRequestOutcome;
  capped: number[];
};
/** A call_briefs row (RPC call_brief, or the FakeRest twin) as the app reads it; anything else is null. */
export function callBriefRowOf(v: unknown): CallBriefRow | null {
  if (typeof v !== "object" || v === null) return null;
  const o = v as Record<string, unknown>;
  const status = o.status === "ok" || o.status === "failed" ? o.status : null;
  const companyId = Number(o.company_id);
  if (!status || !Number.isSafeInteger(companyId)) return null;
  const line = (x: unknown) => (typeof x === "string" && x.trim() ? x : null);
  return {
    companyId,
    whatTheyDo: line(o.what_they_do),
    hook: line(o.hook),
    status,
    generatedAt: typeof o.generated_at === "string" ? o.generated_at : "",
    websiteUsed: o.website_used === true,
  };
}

export interface AiApi {
  draft(i: AiDraftRequest): Promise<AiDraftResult>;
  rejectDraft(id: number, reason: RejectReason | null): Promise<void>;
  rebuildStyle(): Promise<StyleRebuild>;
  saveStyle(profileText: string): Promise<void>;
  /** GAVION ai follow-up (spec §1 « Suivi IA »): « Annuler le suivi » (the plan's rep or an admin). */
  cancelFollowUp(planId: number): Promise<void>;
  /** « Décaler »: the touch moves to 10:00 lead-local on `day` (YYYY-MM-DD); answers the new due time (ISO). */
  moveFollowUpTouch(touchId: number, day: string): Promise<string>;
  /** « Écrire maintenant »: the plan's next touch is written within the minute. */
  writeFollowUpTouchNow(touchId: number): Promise<void>;
  /** GAVION call-brief-pipeline (spec A3): prepare these leads' briefs (current lead first, at most 4). Optional:
   * missing = no brief (« not_configured »). */
  requestCallBriefs?(companyIds: number[]): Promise<CallBriefRequestResult>;
  /** GAVION call-brief-pipeline (spec A4): the lead's stored brief, or null. */
  callBrief?(companyId: number): Promise<CallBriefRow | null>;
  /** Demo only: plan the follow-up of a logged call and write its first touch at once; true when a plan started. */
  demoFollowUp?(i: {
    companyId: number;
    callId: number;
    outcome: string;
  }): Promise<boolean>;
  /**
   * Demo only: the after_call proposed draft right away (the real one is queued by the engine, spec §5). `outcome` is
   * the logged call outcome: brochure_sent builds the draft on the brochure template (owner ruling D5).
   */
  demoAfterCall?(i: {
    companyId: number;
    contactId: number | null;
    callId: number;
    outcome: string | null;
  }): Promise<void>;
}

export class AiApiError extends Error {
  readonly code: string;
  /** ai_unsupported_claim: the blocked figures or words (short strings, never the draft text). */
  readonly detail: string[];
  constructor(code: string, detail: string[] = []) {
    super(code);
    this.name = "AiApiError";
    this.code = code;
    this.detail = detail;
  }
}

/** i18n key of an AI error code (gavion.ai.errors.*). */
export function aiErrorKey(code: string): string {
  switch (code) {
    case "ai_cap_reached":
      return "gavion.ai.errors.cap";
    case "ai_not_configured":
      return "gavion.ai.errors.not_configured";
    case "ai_unsupported_claim":
      return "gavion.ai.errors.unsupported_claim_generic";
    case "forbidden":
      return "gavion.ai.errors.forbidden";
    case "lead_not_found":
      return "gavion.ai.errors.not_found";
    case "not_pending":
    case "ai_draft_not_pending":
    case "ai_draft_not_found":
      return "gavion.ai.errors.not_pending";
    default:
      return "gavion.ai.errors.unavailable";
  }
}

/** The message of an AI error: an unsupported claim names what was blocked (« 20 % »), when known. */
export function aiErrorMessage(
  t: (key: string, o?: Record<string, unknown>) => string,
  e: unknown,
): string {
  const err = e instanceof AiApiError ? e : new AiApiError("ai_unavailable");
  if (err.code === "ai_unsupported_claim" && err.detail.length > 0)
    return t("gavion.ai.errors.unsupported_claim", {
      detail: err.detail.join(" », « "),
    });
  return t(aiErrorKey(err.code));
}
